import React, { useEffect, useMemo, useState } from "react";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { DialogHeader, DialogBody, DialogFooter } from "components/common/dialogs/GenericDialog";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import { ShippingHandleType, CheckoutActions } from "lib/types/checkout";
import { useLocale } from "lib/hooks/useLocale";
import { postRequest, putRequest } from "lib/core/apiClient";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import { appendParamsToItemsUrl, getDefaultShippingHandle } from "lib/utils/checkout";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { mutate } from "swr";
import { analyticsEvents, apiURI } from "lib/utils/constants";
import { redirectUrl } from "lib/utils/helpers";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useCheckoutErrors } from "lib/hooks/useCheckoutErrors";
import { X } from "@phosphor-icons/react";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { getUpsellUrl } from "lib/utils/upsell";
import ShippingHandle from "../shipping-handles/ShippingHandle";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";

interface ShippingHandlesDialogProps { }

const ShippingHandlesDialog: React.FC<ShippingHandlesDialogProps> = ({ }) => {
  const { t } = useLocale();
  const {
    state: { isAuthenticated },
  } = useAuthContext();
  const { closePopup } = usePopupOrchestrator();
  const {
    state: { checkoutMetadata },
  } = useMerchantContext();
  const {
    state: { shippingHandles, checkoutId, isC2P, checkoutView, actionUrls },
    actions: { updateCheckoutBasedOnCheckoutResponse, setShippingHandles, setCheckoutModal, setCheckoutView },
  } = useCheckoutContext();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const { isNotServicable } = useCheckoutErrors();
  const {
    actions: { mutatePayment },
  } = usePaymentContext();

  const [selectedHandle, setSelectedHandle] = useState<ShippingHandleType>({
    id: "",
    handle_name: "",
    price: "",
    selected_handle: false,
    cod_enabled: false,
    cod_extra_price: "0",
    online_enabled: true,
    original_price: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showHandlesDialog, setShowHandlesDialog] = useState<boolean>(false);

  useEffect(() => {
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_SHIPPING_HANDLES_LOADED,
      eventType: "load",
      metaData: {
        shippingData: {
          availableHandles: shippingHandles,
        },
      },
    });
  }, []);

  useEffect(() => {
    if (!Boolean(shippingHandles)) {
      setShowHandlesDialog(false);
      return;
    }

    setShowHandlesDialog(true);
    const defaultShippindHandle: ShippingHandleType | undefined = getDefaultShippingHandle(shippingHandles);
    if (defaultShippindHandle) {
      setSelectedHandle(defaultShippindHandle);
    }
  }, [shippingHandles]);

  const onHandleSelect = async () => {
    if (!selectedHandle.id) return;
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_SHIPPING_HANDLE_SELECTED,
      eventType: "click",
      metaData: {
        shippingData: {
          availableHandles: shippingHandles,
          selectedHandle: selectedHandle,
          isCod: false,
        },
      },
    });

    await updateShippingHandle();
    return;
  };

  const updateShippingHandle = async () => {
    const payload = {
      shipping_handle: selectedHandle.id,
    };
    const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);

    try {
      setIsLoading(true);
      const response = await postRequest(
        `/checkout/${checkoutId}/shipping-handle`,
        payload,
        "KRATOS_PRIVATE",
      );
      handleCheckoutResponse(response);
      closeShippingHandlesDialog();
      mutate(DISCOUNT_PATH);

      if (checkoutView === "PAYMENTS") {
        mutatePayment();
        mutate(`/checkout/${checkoutId}/rewards`);
        if (Boolean(checkoutMetadata?.uiAttributes?.upsell?.isEnabled)) {
          mutate(getUpsellUrl({ checkoutId }));
        }
        mutate(`UPI_INTENT`);
        return;
      }

      if (response?.metadata?.action_urls?.[CheckoutActions.ADDRESS_SELECT]?.success_url) {
        redirectUrl(response.metadata.action_urls[CheckoutActions.ADDRESS_SELECT].success_url);
        return;
      }

      if (
        actionUrls &&
        actionUrls[CheckoutActions.ADDRESS_SELECT] &&
        actionUrls[CheckoutActions.ADDRESS_SELECT].success_url
      ) {
        redirectUrl(actionUrls[CheckoutActions.ADDRESS_SELECT].success_url);
        return;
      }

      setCheckoutView("PAYMENTS");
    } catch (e) {
      console.error(e);
      if (
        actionUrls &&
        actionUrls[CheckoutActions.ADDRESS_SELECT] &&
        actionUrls[CheckoutActions.ADDRESS_SELECT].failure_url
      ) {
        redirectUrl(actionUrls[CheckoutActions.ADDRESS_SELECT].failure_url);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckoutResponse = (checkoutResponse: any) => {
    updateCheckoutBasedOnCheckoutResponse(checkoutResponse);
    const updatedHandles = checkoutResponse?.metadata?.available_shipping_handles ?? [];
    if (Boolean(updatedHandles?.length)) {
      setShippingHandles(updatedHandles);
    }
  };

  const closeShippingHandlesDialog = () => {
    closePopup();
  };

  const hasNoHandlesToShow = !Boolean(shippingHandles) || isNotServicable;

  useEffect(() => {
    if (hasNoHandlesToShow) closePopup();
  }, [hasNoHandlesToShow]);

  if (hasNoHandlesToShow) return <></>;

  return (
    <>
      {Boolean(showHandlesDialog) && (
        <>
          <DialogHeader>
            <div className="flex h-full w-full flex-row items-center justify-between">
              <h1 className="text-base font-medium"> {t("shipping_handles_dialog_header")}</h1>
              {!Boolean(isC2P) && (
                <button className="outline-none">
                  <X className="h-5 w-5 cursor-pointer text-coal-dark" onClick={closeShippingHandlesDialog} />
                </button>
              )}
            </div>
          </DialogHeader>

          <DialogBody>
            <ul className="flex flex-col gap-3 max-h-80 overflow-y-scroll pb-28 scrollbar-hide">
              {shippingHandles.map((handle) => (
                <li
                  key={handle?.id}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedHandle(handle);
                  }}>
                  <ShippingHandle
                    id={handle?.id}
                    name={`${handle.handle_name}`}
                    etdText={handle.etd_text}
                    minDays={handle.min_days_to_deliver}
                    maxDays={handle.max_days_to_deliver}
                    charge={handle.price}
                    isChecked={selectedHandle.id === handle?.id}
                    codEnabled={handle.cod_enabled}
                    codExtraPrice={parseInt(handle.cod_extra_price ?? "0")}
                    onlineEnabled={handle.online_enabled}
                    originalPrice={handle.original_price}
                  />
                </li>
              ))}
            </ul>
          </DialogBody>

          <DialogFooter>
            <PrimaryButton
              buttonText={t("proceed_to_pay")}
              id="flo_shipping_handles_select"
              onClick={onHandleSelect}
              height="h-14"
              isCheckout={true}
              isLoading={isLoading}
            />
          </DialogFooter>
        </>
      )}

      {isLoading && <OverlaySpinner />}
    </>
  );
};

export default ShippingHandlesDialog;
